import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLingui } from '@lingui/react'
import { Trans, msg } from '@lingui/macro'
import {
  Alert
  , Box
  , Button
  , FormControlLabel
  , IconButton
  , InputAdornment
  , LinearProgress
  , MenuItem
  , Snackbar
  , styled
  , Switch
  , TextField
} from '@mui/material/'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { MuiTelInput } from 'mui-tel-input'
import { ThemeContext } from '../../App'
import { NewApiBaseUrl } from "../../consts/new-api-base-url";
import useHttpApiNew from "../../hooks/useHttpApiNew";
import { useActionLogs } from '../../store/actionLogsContext';
import SvgCustomIcon from "../icons/SvgCustomIcon";
import { useTheme } from "@mui/material";
import { appFlatTextFieldSx } from "../../consts/app-flat-text-field-sx";

const organizationsGetListUrl = new URL(`${NewApiBaseUrl}/Organization`)
const modifyUserUrl = new URL(`${NewApiBaseUrl}/Auth/Modify`)
const addUserUrl = new URL(`${NewApiBaseUrl}/Auth/Add`)

const SIGNUP_WATCH_FIELDS = [
  'name',
  'surname',
  'organization',
  'department',
  'username',
  'phone',
  'email',
  'password',
  'confirmPassword'
]

/** Same rules as Controllers — sync only, no trigger() (avoids main-thread jank after API responses). */
const SIGNUP_EMAIL_RE =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
const SIGNUP_PHONE_RE = /^\+[0-9 ]{6,16}$/
const SIGNUP_PASSWORD_COMPLEXITY_RE = /(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])./

function signupWatchArrayToObject(watched) {
  const arr = Array.isArray(watched) ? watched : []
  return SIGNUP_WATCH_FIELDS.reduce((acc, key, i) => {
    acc[key] = arr[i]
    return acc
  }, {})
}

/** True when the organization select has a value (any id: numeric, UUID string, etc.). */
function isOrganizationSelected(raw) {
  if (raw === '' || raw === null || raw === undefined) return false
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0
  return String(raw).trim() !== ''
}

function isSignupFormReadyForSubmit(v) {
  if (!String(v.name ?? '').trim()) return false
  if (!String(v.surname ?? '').trim()) return false
  if (!isOrganizationSelected(v.organization)) return false
  if (!String(v.department ?? '').trim()) return false
  if (!String(v.username ?? '').trim()) return false
  const phoneStr = typeof v.phone === 'string' ? v.phone : v.phone != null ? String(v.phone) : ''
  if (!SIGNUP_PHONE_RE.test(phoneStr.trim())) return false
  const emailStr = String(v.email ?? '').trim()
  if (emailStr && !SIGNUP_EMAIL_RE.test(emailStr)) return false
  const pwd = String(v.password ?? '')
  if (pwd.length < 8 || !SIGNUP_PASSWORD_COMPLEXITY_RE.test(pwd)) return false
  if (String(v.confirmPassword ?? '').trim() !== pwd.trim()) return false
  return true
}

export const MaterialUISwitch = styled(Switch)(({ theme }) => ({
  width: 45,
  height: 24,
  padding: 6,
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(4px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateX(16px)',
      '& .MuiSwitch-thumb:before': {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="14" width="14" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          '#fff'
        )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`
      },
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? '#003892' : theme.palette.newColors.pink,
    width: 22,
    height: 22,
    '&::before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="14" width="14" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
        '#fff'
      )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
    },
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor: theme.palette.mode === 'dark' ? '#8796A5' : '#aab4be',
    borderRadius: 8,
  },
}));

export default function UserForm({ isMobile, token, user, compactSignup = false }) {
  const { _ } = useLingui()
  const { control, formState, handleSubmit, setValue, watch, reset, getValues } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })
  const signupWatched = useWatch({ control, name: SIGNUP_WATCH_FIELDS })
  const signupFormValid = useMemo(() => {
    if (user) return true
    return isSignupFormReadyForSubmit(signupWatchArrayToObject(signupWatched))
  }, [user, signupWatched])
  const passwordRef = useRef()
  const confirmPasswordRef = useRef()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  /** Sblocco campo username su signup: evita autofill browser fino al focus. */
  const [signupUsernameUnlocked, setSignupUsernameUnlocked] = useState(false)
  const [organizations, setOrganizations] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
    key: 0
  })
  const theme = useTheme()

  /** Single entry for toasts — one setState, no useEffect chains. */
  const notify = useCallback((severity, message) => {
    setSnackbar(prev => ({
      open: true,
      message,
      severity,
      key: prev.key + 1
    }))
  }, [])


  const handleMouseDownPassword = event => { event.preventDefault() }

  const watchOrganization = watch('organization')
  const watchName = watch('name')
  const watchSurname = watch('surname')
  const { mode, toggleTheme } = useContext(ThemeContext)
  const { request } = useHttpApiNew()
  const { fetchLogs } = useActionLogs()

  {/*
  const getOrganizations = useCallback(() => {
    sendRequest({
      url             : organizationsUrl
      , manageResData : res => {
        setOrganizations(res.organizations)
      }
    })
  }, [sendRequest])
  */}

  const getOrganizationsList = useCallback(async () => {
    /*
    fetch(organizationsGetListUrl,{
      method: 'GET',
      headers : { Authorization: `Bearer ${token}` },
    }).then(res => res.json())
        .then(response => {
          if (response.responseAnyError) return;
          setOrganizations(response.responseData);
        });
     */
    const response = await request(organizationsGetListUrl, 'GET', null, { Authorization: `Bearer ${token}` })
    if (response.responseAnyError) return;
    setOrganizations(response.responseData);
  })

  useEffect(() => {
    getOrganizationsList()
  }, [])

  useEffect(() => {
    if (compactSignup) setSignupUsernameUnlocked(false)
  }, [compactSignup])

  useEffect(() => {
    if (user && Object.keys(user).length) reset(user)
  }, [user, reset])

  // watch(['name','surname']) returns a new array every render → this effect must use stable deps
  // or it re-runs every frame, setValue+shouldValidate freezes the page.
  useEffect(() => {
    if (user) return
    const name = String(watchName ?? '').trim()
    const surname = String(watchSurname ?? '').trim()
    if (!name || !surname) return
    const nextUsername = `${name}.${surname}`.toLowerCase()
    if (getValues('username') === nextUsername) return
    setValue('username', nextUsername, { shouldValidate: true, shouldDirty: false })
  }, [user, watchName, watchSurname, setValue, getValues])

  useEffect(() => {
    if (!isOrganizationSelected(watchOrganization)) {
      setValue('department', '', { shouldValidate: true, shouldDirty: false })
    }
  }, [watchOrganization, setValue])

  const notifyApiError = apiResponse => {
    const text =
      typeof apiResponse?.responseMessage === 'string' && apiResponse.responseMessage.trim()
        ? apiResponse.responseMessage
        : _(msg`Request failed`)
    notify('error', text)
  }

  const onSubmit = async data => {
    if (Object.keys(formState.errors).length) return
    let body = {}
    if (user) {
      if (!Object.keys(formState.dirtyFields).length) return
      Object.keys(formState.dirtyFields)
        .forEach(key => {
          body = {
            ...body
            , [key]: key === 'theme'
              ? data[key]
                ? 'dark'
                : 'light'
              : data[key]
          }
        })
      const dataToSend = Object.assign(data,body)
      setIsSubmitting(true)
      try {
        const response = await request(modifyUserUrl, 'PATCH', dataToSend, { Authorization: `Bearer ${token}` })
        if (!response || response.responseAnyError) {
          if (response?.responseAnyError) notifyApiError(response)
          return
        }
        fetchLogs()
        const stored = JSON.parse(localStorage.getItem('adams_user'))
        stored.theme = body.theme
        localStorage.setItem('adams_user', JSON.stringify(stored))
        reset(dataToSend)
        notify('success', <Trans>User succefully modified!</Trans>)
      } finally {
        setIsSubmitting(false)
      }
    } else {
      body = data
      setIsSubmitting(true)
      try {
        const response = await request(addUserUrl, 'POST', body, { Authorization: `Bearer ${token}` })
        if (!response || response.responseAnyError) {
          if (response?.responseAnyError) notifyApiError(response)
          return
        }
        notify(
          'success',
          <Trans>
            User succefully recorded! Please <Link to='/'>Login</Link>
          </Trans>
        )
        reset()
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const fieldSx = compactSignup
    ? {
        ...appFlatTextFieldSx(theme),
        '& .MuiFormHelperText-root': { mt: 0.25, mb: 0 },
      }
    : appFlatTextFieldSx(theme)
  const fieldMargin = compactSignup ? 'dense' : isMobile ? 'dense' : 'normal'
  const signupLabelShrink = compactSignup ? { InputLabelProps: { shrink: true } } : {}
  const formTopMargin = compactSignup ? 0.5 : '2rem'

  return (
    <>
    <Box
      width='100%'
      display='flex'
      flexDirection='column'
      alignItems='center'
      component='form'
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      autoComplete={compactSignup ? 'off' : undefined}
      sx={{ position: 'relative', mt: formTopMargin }}
    >
      {compactSignup && (
        <Box
          component='span'
          sx={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}
          aria-hidden
        >
          <input type="text" name="prevent_autofill_user" autoComplete="username" tabIndex={-1} readOnly />
          <input type="password" name="prevent_autofill_pass" autoComplete="current-password" tabIndex={-1} readOnly />
        </Box>
      )}

      <Controller
        name='name'
        control={control}
        defaultValue=""
        render={({ field, fieldState }) => (
          <TextField
            margin={fieldMargin}
            required={!token}
            fullWidth
            label={_(msg`Name`)}
            autoComplete={compactSignup ? 'given-name' : 'name'}
            autoFocus={!compactSignup}
            sx={fieldSx}
            error={!!fieldState.error}
            helperText={fieldState.error ? fieldState.error.message : null}
            {...signupLabelShrink}
            {...field}
          />
        )}
        rules={{ required: _(msg`Name is required`) }}
      />
      <Controller
        name='surname'
        control={control}
        defaultValue=""
        render={({ field, fieldState }) => (
          <TextField
            margin={fieldMargin}
            required={!token}
            fullWidth
            label={_(msg`Surname`)}
            autoComplete={compactSignup ? 'family-name' : 'surname'}
            sx={fieldSx}
            error={!!fieldState.error}
            helperText={fieldState.error ? fieldState.error.message : null}
            {...signupLabelShrink}
            {...field}
          />
        )}
        rules={{ required: _(msg`Surname is required`) }}
      />
      <Controller
        name='organization'
        control={control}
        defaultValue=""
        render={({ field, fieldState }) => (
          <TextField
            margin={fieldMargin}
            required={!token}
            disabled={token}
            fullWidth
            label={_(msg`Organization`)}
            autoComplete={compactSignup ? 'off' : 'organization'}
            select
            sx={fieldSx}
            error={!!fieldState.error}
            helperText={fieldState.error ? fieldState.error.message : null}
            {...signupLabelShrink}
            {...field}
          >
            {organizations.map(organization => (
              <MenuItem key={organization.id} value={organization.id}>
                {organization.description}
              </MenuItem>
            ))}
          </TextField>
        )}
        rules={{
          validate: value =>
            isOrganizationSelected(value) || _(msg`Organization is required`)
        }}
      />
      {isOrganizationSelected(watchOrganization) && (
        <Controller
          name='department'
          control={control}
          defaultValue=""
          render={({ field, fieldState }) => (
            <TextField
              margin={fieldMargin}
              required={!token}
              fullWidth
              label={_(msg`Department / Unit`)}
              autoComplete={compactSignup ? 'off' : 'department'}
              sx={fieldSx}
              error={!!fieldState.error}
              helperText={fieldState.error ? fieldState.error.message : null}
              {...signupLabelShrink}
              {...field}
            />
          )}
          rules={{
            required: _(msg`Department / Unit is required`)
          }}
        />
      )}
      <Controller
        name='username'
        control={control}
        defaultValue=''
        render={({ field, fieldState }) => (
          <TextField
            margin={fieldMargin}
            required={!token}
            disabled={token}
            fullWidth
            label={_(msg`username`)}
            autoComplete={compactSignup ? 'off' : 'username'}
            sx={fieldSx}
            error={!!fieldState.error}
            helperText={fieldState.error ? fieldState.error.message : null}
            {...signupLabelShrink}
            {...field}
            inputProps={{
              ...field.inputProps,
              autoComplete: 'off',
              readOnly: compactSignup ? !signupUsernameUnlocked : false,
            }}
            onFocus={e => {
              if (compactSignup) setSignupUsernameUnlocked(true)
              field.onFocus?.(e)
            }}
          />
        )}
        rules={{ required: _(msg`Username is required`) }}
      />
      <Controller
        name='phone'
        control={control}
        defaultValue=""
        render={({ field, fieldState }) => (
          <MuiTelInput
            // <TextField
            placeholder={_(msg`+xxx xxxxxx`)}
            margin={fieldMargin}
            required={!token}
            fullWidth
            label={_(msg`Telephone number`)}
            autoComplete={compactSignup ? 'off' : 'tel'}
            sx={fieldSx}
            error={!!fieldState.error}
            helperText={fieldState.error ? fieldState.error.message : null}
            {...signupLabelShrink}
            {...field}
          />
        )}
        rules={{
          required: _(msg`Telephone number is required`)
          , pattern: { value: /^\+[0-9 ]{6,16}$/, message: _(msg`Telephone number must be a number between 6 and 16 digits`) }
        }}
      />
      <Controller
        name='email'
        control={control}
        defaultValue=""
        render={({ field, fieldState }) => (
          <TextField
            margin={fieldMargin}
            fullWidth
            label={_(msg`Email`)}
            autoComplete={compactSignup ? 'off' : 'email'}
            sx={fieldSx}
            error={!!fieldState.error}
            helperText={fieldState.error ? fieldState.error.message : null}
            {...signupLabelShrink}
            {...field}
          />
        )}
        rules={{
          validate: v => {
            const s = v == null ? '' : String(v).trim()
            if (!s) return true
            const emailRe =
              /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            return emailRe.test(s) ? true : _(msg`Email address is non valid`)
          }
        }}
      />
      <Controller
        name='password'
        control={control}
        defaultValue=""
        render={({ field, fieldState }) => (
          <TextField
            inputRef={passwordRef}
            margin={fieldMargin}
            required={!token}
            fullWidth
            label={_(msg`New password`)}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            sx={fieldSx}
            {...signupLabelShrink}
            InputProps={{
              endAdornment: <InputAdornment position='end'>
                <IconButton
                  aria-label='toggle password visibility'
                  onClick={() => setShowPassword(v => !v)}
                  onMouseDown={handleMouseDownPassword}
                  edge='end'
                >
                  {showPassword
                    ? <VisibilityOff sx={{ color: theme.palette.newColors.gray }} />
                    : <SvgCustomIcon name={'visibility'} color={theme.palette.newColors.gray} />}
                </IconButton>
              </InputAdornment>
            }}
            error={!!fieldState.error}
            helperText={fieldState.error ? fieldState.error.message : null}
            {...field}
          />
        )}
        rules={{
          minLength: { value: 8, message: _(msg`Password must be at least 8 characters`) }
          , pattern: {
            value: /(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])./ // (?!.* ) no spaces
            , message: _(msg`Password must contain at least a lowercase letter, an upper case letter and a digit`)
          }
        }}
      />
      <Controller
        name='confirmPassword'
        control={control}
        defaultValue=""
        render={({ field, fieldState }) => (
          <TextField
            inputRef={confirmPasswordRef}
            margin={fieldMargin}
            required={!token}
            fullWidth     
            label={_(msg`Confirm new password`)}
            type={showConfirmPassword ? 'text' : 'password'}
            autoComplete="new-password"
            sx={fieldSx}
            {...signupLabelShrink}
            InputProps={{
              endAdornment: <InputAdornment position='end'>
                <IconButton
                  aria-label='toggle password visibility'
                  onClick={() => setShowConfirmPassword(v => !v)}
                  onMouseDown={handleMouseDownPassword}
                  edge='end'
                >
                  {showConfirmPassword
                    ? <VisibilityOff sx={{ color: theme.palette.newColors.gray }} />
                    : <SvgCustomIcon name={'visibility'} color={theme.palette.newColors.gray} />}
                </IconButton>
              </InputAdornment>
            }}
            error={!!fieldState.error}
            helperText={fieldState.error ? fieldState.error.message : null}
            {...field}
          />
        )}
        rules={{
          minLength: { value: 8, message: _(msg`Password must be at least 8 characters`) }
          , validate: (value, { password }) => value.trim() === password.trim() || _(msg`Passwords must match`)
          , pattern: {
            value: /(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])./ // (?!.* ) no spaces
            , message: _(msg`Password must contain at least an lowercase letter, an upper case letter and a digit`)
          }
          // validate   : (value, formValues) => bool || msg
          // formValues are actual form values by name, can be destructured
        }}
      />
      {isSubmitting && <LinearProgress sx={{ mt: compactSignup ? 0.5 : 2 }} />}
  
      <Button
        type='submit'
        fullWidth={isMobile}
        variant='contained'
        color='primary'
        sx={{
          mt: compactSignup ? 1 : 3,
          mb: compactSignup ? 0.5 : 2,
          width: '100%',
          background: theme.palette.newColors.pinkGradient,
          color: '#fff',
          '&.Mui-disabled': {
            background: '#ccc',
            color: '#666',
          },
        }}
        disabled={
          isSubmitting ||
          (user
            ? Boolean(token && !Object.keys(formState.dirtyFields).length)
            : !signupFormValid)
        }
      >
        {user ? <Trans>Save</Trans> : <Trans>Sign up</Trans>}
      </Button>
    </Box>

        <Snackbar
        key={snackbar.key}
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 3000 : 6000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return
          setSnackbar(prev => ({ ...prev, open: false }))
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </>
  )
}

// Example of validation schema using YUP
// const schema = yup.object().shape({
//   name              : yup.string().required(_(msg`Name is required`))
//   , surname         : yup.string().required(_(msg`Surname is required`))
//   , department      : yup.string().required()
//   , hospital        : yup.string().required(_(msg`Hospital/Health care facility is required`))
//   , phone           : yup.number(_(msg`Telephone number must be a number`)).positive().integer().required(_(msg`Telephone number is required`))
//   , password        : yup.string().required(_(msg`Password is required`))
//   , confirmPassword : yup.string().oneOf([yup.ref('password'), null], _(msg`Passwords must match`))
// })

// const { control, handleSubmit, setError, reset: resetForm } = useForm({
//   resolver: yupResolver(schema)
// })

// Solution to reset error on two related field simultaneously

// const watchEitherDeptOrHosp = watch(['department', 'hospital'])

// useEffect(() => {
//   if (watchEitherDeptOrHosp.some(field => field?.trim())) {
//     clearErrors(['department', 'hospital'])
//   }
// }, [watchEitherDeptOrHosp, clearErrors])

// Field validation
// rules={{
//   // validate: (value, { hospital }) => !!value || !!hospital || _(msg`At least one of these fields is required`)
// }}
// validate: (value, { department }) => !!value || !!department || _(msg`At least one of these fields is required`)
